/**
 * Focus Management Utilities
 *
 * This module provides React hooks and utilities for managing focus behavior
 * in the application, including:
 * - Auto-focusing elements
 * - Tab navigation trapping
 * - Focus event detection (focusin/focusout)
 * - Disabling tabbing in content areas
 * - Focus management for highlights
 *
 * These utilities help ensure proper keyboard navigation and accessibility.
 */

import type {
  Element,
  FocusEvent,
  HTMLElement,
  KeyboardEvent,
  Event,
  Range,
} from '@openstax/types/lib.dom';
import React from 'react';
import { Highlight } from '@openstax/highlighter';
import { addSafeEventListener } from '../domUtils';
import { isElement } from '../guards';
import { assertDocument, assertWindow } from '../utils';

export const useDrawFocus = <E extends HTMLElement = HTMLElement>() => {
  const ref = React.useRef<E>(null);

  React.useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus();
    }
  }, [ref]);

  return ref;
};

export const focusableItemQuery = ([
  'button',
  'input',
  'select',
  'textarea',
  '[href]',
  '[tabindex]:not([tabindex=\'-1\'])',
])
  .map((s) => (s.includes('[') ? s : `${s}:not([disabled])`))
  .join(',');

// Ring buffer arithmetic for circular navigation
function ringAdd(arr: unknown[], a: number, b: number) {
  return (arr.length + a + b) % arr.length;
}

// Helper type for focusable element entries
interface FocusableEntry {
  container: HTMLElement;
  firstEl: HTMLElement;
  lastEl: HTMLElement;
  allElements: HTMLElement[];
}

// Saves the current text selection for later restoration (important for Firefox)
function saveTextSelection(win: Window): Range | null {
  const selection = win.getSelection();
  return selection && selection.rangeCount > 0
    ? selection.getRangeAt(0).cloneRange()
    : null;
}

// Restores a previously saved text selection
function restoreTextSelection(win: Window, savedRange: Range | null): void {
  if (savedRange) {
    try {
      const sel = win.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRange);
      }
    } catch (e) {
      // Ignore restoration errors
    }
  }
}

// Schedules selection restoration after browser processes Tab event
function scheduleSelectionRestoration(win: Window, restoreFn: () => void): void {
  if (typeof win.requestAnimationFrame === 'function') {
    win.requestAnimationFrame(restoreFn);
  } else {
    win.setTimeout(restoreFn, 0);
  }
}

// Determines the next focus element when Tab wraps around
function getNextWrapElement(
  currentIndex: number,
  isShiftKey: boolean,
  focusableElements: FocusableEntry[],
  currentEntry: FocusableEntry
): { nextElement: HTMLElement | null; shouldPreventDefault: boolean } {
  if (isShiftKey) {
    if (currentIndex <= 0) {
      // At or before first element, wrap to last
      const feIdx = focusableElements.indexOf(currentEntry);
      const newIdx = ringAdd(focusableElements, feIdx, -1);
      return {
        nextElement: focusableElements[newIdx].lastEl,
        shouldPreventDefault: true,
      };
    }
  } else {
    if (currentIndex >= currentEntry.allElements.length - 1) {
      // At or after last element, wrap to first
      const feIdx = focusableElements.indexOf(currentEntry);
      const newIdx = ringAdd(focusableElements, feIdx, 1);
      return {
        nextElement: focusableElements[newIdx].firstEl,
        shouldPreventDefault: true,
      };
    }
  }
  return { nextElement: null, shouldPreventDefault: false };
}

// Query focusable elements within containers
function queryFocusableInContainers(containers: HTMLElement[]): FocusableEntry[] {
  return containers
    .map((container) => {
      const contents = Array.from(
        container.querySelectorAll<HTMLElement>(focusableItemQuery)
      );

      return {
        container,
        firstEl: contents[0],
        lastEl: contents[contents.length - 1],
        allElements: contents,
      };
    })
    .filter((c) => c.firstEl);
}

// Safely saves the current text selection, returning null if unavailable
function safeSaveTextSelection(win: Window): Range | null {
  try {
    return saveTextSelection(win);
  } catch (e) {
    // Selection API unavailable or restricted context
    return null;
  }
}

// Creates a tab navigation trap that cycles focus within container elements
// Based on https://hidde.blog/using-javascript-to-trap-focus-in-an-element/
// IMPORTANT: Preserves text selection on Firefox when Tab navigation occurs
export function createTrapTab(...elements: HTMLElement[]) {
  const containers = elements
    .filter((c) => c && 'querySelectorAll' in c); // in some tests, this gets garbage

  if (containers.length === 0) {
    return () => null;
  }

  const win = assertWindow();

  return (event: KeyboardEvent) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = queryFocusableInContainers(containers);

    if (focusableElements.length === 0) {
      return;
    }

    const savedRange = safeSaveTextSelection(win);
    const restoreSelection = () => restoreTextSelection(win, savedRange);

    // Keep track of where we came from
    const startEl = document?.activeElement as HTMLElement;
    const feEntry = focusableElements.find((entry) =>
      entry.container.contains(startEl)
    );

    // Focus has escaped the trap
    if (!feEntry) {
      focusableElements[0].firstEl.focus();
      event.preventDefault();
      restoreSelection();
      return;
    }

    // Check if we need to wrap around (focus is at a boundary)
    const currentIndex = feEntry.allElements.indexOf(startEl);
    const { nextElement, shouldPreventDefault } = getNextWrapElement(
      currentIndex,
      event.shiftKey,
      focusableElements,
      feEntry
    );

    // If we're wrapping around, handle focus and restore selection
    if (nextElement && shouldPreventDefault) {
      nextElement.focus();
      event.preventDefault();
      restoreSelection();
      return;
    }

    // For normal Tab navigation within the same container,
    // let the browser handle it but restore selection after
    scheduleSelectionRestoration(win, restoreSelection);
  };
}

// Focuses the first focusable element while preserving text selection
// Used for modals/overlays that need initial focus but preserve user's text selection
function autoFocusFirstElement(el: HTMLElement): void {
  const win = assertWindow();
  const savedRange = safeSaveTextSelection(win);

  const focusableElements = Array.from(
    el.querySelectorAll<HTMLElement>(focusableItemQuery)
  );

  if (focusableElements.length > 0) {
    const currentFocus = assertDocument().activeElement;
    const isElementFocused = currentFocus && el.contains(currentFocus);

    if (!isElementFocused) {
      focusableElements[0].focus();
      restoreTextSelection(win, savedRange);
    }
  }
}

// Supply otherDep when focusable elements might change (see EditCard)
// Set autoFocus=true to focus the first focusable element on mount (useful for modals/overlays)
export function useTrapTabNavigation(
  ref: React.MutableRefObject<HTMLElement | null>,
  otherDep?: unknown,
  autoFocus?: boolean
) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el?.addEventListener) {
      return;
    }

    // Auto-focus first element if requested (for modals/overlays)
    if (autoFocus) {
      try {
        autoFocusFirstElement(el);
      } catch (e) {
        // Ignore errors in SSR or when window is not available
      }
    }

    const trapTab = createTrapTab(el);

    el.addEventListener('keydown', trapTab, true);

    return () => el.removeEventListener('keydown', trapTab, true);
  }, [ref, otherDep, autoFocus]);
}

export const onFocusInOrOutHandler =
  (
    ref: React.RefObject<HTMLElement>,
    isEnabled: boolean,
    cb: () => void,
    type: 'focusin' | 'focusout'
  ) =>
  () => {
    const el = ref?.current;
    if (!el) {
      return;
    }

    const handler = (event: FocusEvent) => {
      const target =
        type === 'focusout'
          ? (event.relatedTarget ?? event.target)
          : event.target;

      if (
        type === 'focusout' &&
        (!isElement(target) || !(ref.current as HTMLElement).contains(target))
      ) {
        cb();
      } else if (
        type === 'focusin' &&
        isElement(target) &&
        (ref.current as HTMLElement).contains(target)
      ) {
        cb();
      }
    };

    if (isEnabled) {
      return addSafeEventListener(el, type, handler);
    }
  };

export const useFocusLost = (
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean,
  cb: () => void
) => {
  React.useEffect(
    () => onFocusInOrOutHandler(ref, isEnabled, cb, 'focusout')(),
    [ref, isEnabled, cb]
  );
};

export const useFocusIn = (
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean,
  cb: () => void
) => {
  React.useEffect(
    () => onFocusInOrOutHandler(ref, isEnabled, cb, 'focusin')(),
    [ref, isEnabled, cb]
  );
};

// Based on https://stackoverflow.com/questions/1599660/which-html-elements-can-receive-focus/30753870#30753870
// and https://allyjs.io/data-tables/focusable.html
const tabbableElementsSelector = [
  'a[href]',
  'area[href]',
  'audio',
  'button:not([disabled])',
  'details',
  'embed',
  'iframe',
  'input:not([disabled])',
  'select:not([disabled])',
  'summary',
  'textarea:not([disabled])',
  'object',
  // In Firefox elements with overflow: auto / scroll are tabbable if they are scrollable
  // and ToC is one of these elements so we add `ol` to this list.
  'ol',
  'video',
  '[contentEditable=true]',
  '[tabindex]',
]
  .map((el) => el + `:not([tabindex='-1'])`)
  .join(',');

// Disables tabbing to content behind modals
export const disableContentTabbingHandler = (isEnabled: boolean) => () => {
  if (!isEnabled) {
    return;
  }
  const root = assertDocument().querySelector('#root');
  if (!root) {
    return;
  }

  root.setAttribute('aria-hidden', 'true');
  const tabbable = root.querySelectorAll(tabbableElementsSelector);

  tabbable.forEach((el) => {
    const currentTabIndex = el.getAttribute('tabindex');
    el.setAttribute('tabindex', '-1');
    if (currentTabIndex) {
      el.setAttribute('data-prev-tabindex', currentTabIndex);
    }
  });

  return () => {
    root.removeAttribute('aria-hidden');
    tabbable.forEach((el) => {
      const prevTabIndex = el.getAttribute('data-prev-tabindex');
      if (prevTabIndex) {
        el.setAttribute('tabindex', prevTabIndex);
        el.removeAttribute('data-prev-tabindex');
      } else {
        el.removeAttribute('tabindex');
      }
    });
  };
};

export const useDisableContentTabbing = (isEnabled: boolean) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(disableContentTabbingHandler(isEnabled), [isEnabled]);
};

export const useFocusElement = (
  element: React.RefObject<HTMLElement>,
  shouldFocus: boolean
) => {
  React.useEffect(() => {
    if (shouldFocus && element.current) {
      element.current.focus();
    }
  }, [element, shouldFocus]);
};

export const useFocusHighlight = (
  showCard: (id: string) => void,
  highlights: Highlight[]
) => {
  const document = assertDocument();
  React.useEffect(() => {
    if (!highlights || highlights.length === 0) return;
    const handler = (event: Event) => {
      let target: EventTarget | null;
      if (event.type === 'click') {
        if (isElement(event.target)) {
          /*
            When clicking on a highlight, the target is a mark element and
            we need to find the first span inside it to get the highlight as expected
          */
          target = event.target.querySelector('span');
        }
      } else {
        target = event.target;
      }
      const highlight = highlights.find(
        (h) =>
          h.elements &&
          (h.elements as Element[]).some(
            (el) =>
              el === target ||
              (!!el &&
                typeof el.contains === 'function' &&
                el.contains(target as Element))
          )
      );

      if (highlight) {
        showCard(highlight.id);
      }
    };

    // Listen for focusin and click events to show the card
    // For some reason when focus using click, the focused element is div main-content
    document.addEventListener('focusin', handler);
    document.addEventListener('click', handler);
    return () => {
      document.removeEventListener('focusin', handler);
      document.removeEventListener('click', handler);
    };
  }, [document, highlights, showCard]);
};
